import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Target,
  Brain,
  Trophy,
  Loader2,
  ChevronRight,
  Calendar,
  Clock,
  BookOpen,
  Shield,
  Zap,
  ArrowLeft,
  Download,
  CheckCircle2,
  Play,
  FileText
} from 'lucide-react';
import api from '../services/api';

// Winter '26 Cohort Launch Date
const COHORT_LAUNCH_DATE = new Date('2026-01-08T09:00:00-05:00');

// 30-Day Sprint Weeks (the official timeline)
const SPRINT_WEEKS = [
  {
    week: 1,
    title: 'The Audit & Alignment',
    description: 'We take the Combine results and set the "Target Power Score."',
    icon: Target,
    color: 'orange'
  },
  {
    week: 2,
    title: 'The Academic Push',
    description: 'Heavy integration with the IXL Engine to master the specific Math/ELA modules required for your grade level.',
    icon: BookOpen,
    color: 'blue'
  },
  {
    week: 3,
    title: 'The Legacy Build',
    description: 'Developing the "Standard-Bearer" brand, media training, and social media audit.',
    icon: Shield,
    color: 'purple'
  },
  {
    week: 4,
    title: 'The Clearance',
    description: 'Final testing, re-taking the Combine, and receiving the "Officially Cleared" Certification.',
    icon: Trophy,
    color: 'green'
  }
];

const ThirtyDaySprintEnrollment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [sprintProgress, setSprintProgress] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    checkEnrollmentStatus();
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const updateCountdown = () => {
    const now = new Date();
    const diff = COHORT_LAUNCH_DATE - now;
    
    if (diff <= 0) {
      setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, launched: true });
    } else {
      setTimeRemaining({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        launched: false
      });
    }
  };

  const checkEnrollmentStatus = async () => {
    try {
      const token = localStorage.getItem('academy_token');
      if (token) {
        const userData = localStorage.getItem('academy_user');
        if (userData) {
          const user = JSON.parse(userData);
          setEnrolled(user.is_student || user.sprint_enrolled);
        }
      }
    } catch (error) {
      console.error('Error checking enrollment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = () => {
    navigate('/academy/enroll?program=sprint');
  };

  const handleDownloadPlaybook = () => {
    toast.success('Sprint Playbook downloading...');
    // 30 Day Roadmap PDF
    window.open('https://customer-assets.emergentagent.com/job_scholar-sprint-hub/artifacts/su5i7kvp_30%20Day%20Roadmap.pdf.pdf', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Header with Logo */}
      <div className="bg-gradient-to-b from-gray-900 to-black py-12 border-b border-orange-500/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          {/* Official SAA Logo */}
          <img 
            src="/images/scholar-athlete-logo-new.png" 
            alt="Scholar Athlete Academy" 
            className="mx-auto h-32 md:h-40 lg:h-48 w-auto mb-6 drop-shadow-2xl"
            style={{ filter: 'drop-shadow(0 0 30px rgba(249,115,22,0.4))' }}
          />
          
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            THE <span className="text-orange-500">30-DAY PIVOT</span> STARTS NOW
          </h1>
          <p className="text-xl text-gray-300 italic mb-2">
            Connecting Elite Athletics to Elite Academics.
          </p>
          <p className="text-gray-400 max-w-2xl mx-auto">
            The Pre-Season for your collegiate and NIL career. Targeting National AAU programs, 
            elite private schools, and Collegiate scouts.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* 4-Step Onboarding Path */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8">
          <CardContent className="p-8">
            <h3 className="text-xl font-black text-white mb-6 text-center">YOUR JOURNEY TO CLEARANCE</h3>
            
            <div className="grid md:grid-cols-4 gap-4">
              {/* Step 1 */}
              <div className="text-center p-4 bg-black/40 rounded-lg border border-orange-500/20">
                <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-orange-500 font-black text-lg">1</span>
                </div>
                <h4 className="text-orange-500 font-bold text-sm mb-1">THE AUDIT</h4>
                <p className="text-gray-400 text-xs">Take the Free Diagnostic</p>
              </div>
              
              {/* Step 2 */}
              <div className="text-center p-4 bg-black/40 rounded-lg border border-blue-500/20">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-500 font-black text-lg">2</span>
                </div>
                <h4 className="text-blue-500 font-bold text-sm mb-1">THE SCORE</h4>
                <p className="text-gray-400 text-xs">Reveal your Power Score</p>
              </div>
              
              {/* Step 3 */}
              <div className="text-center p-4 bg-black/40 rounded-lg border border-purple-500/20">
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-purple-500 font-black text-lg">3</span>
                </div>
                <h4 className="text-purple-500 font-bold text-sm mb-1">THE PIVOT</h4>
                <p className="text-gray-400 text-xs">Select your path: $49, $99, or $199/mo</p>
              </div>
              
              {/* Step 4 */}
              <div className="text-center p-4 bg-black/40 rounded-lg border border-green-500/20">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-500 font-black text-lg">4</span>
                </div>
                <h4 className="text-green-500 font-bold text-sm mb-1">THE SPRINT</h4>
                <p className="text-gray-400 text-xs">Execute the 30-Day Training</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Countdown Clock */}
        <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/5 border-2 border-orange-500/30 mb-8">
          <CardContent className="p-8">
            <div className="text-center">
              <h2 className="text-xl font-bold text-orange-500 mb-2">WINTER '26 COHORT</h2>
              <p className="text-gray-400 mb-6">
                {timeRemaining?.launched 
                  ? 'The cohort has launched! Enrollment is open.'
                  : 'Countdown to Launch'}
              </p>
              
              {!timeRemaining?.launched && (
                <div className="flex justify-center gap-4 mb-6">
                  <div className="bg-black/50 rounded-xl p-4 min-w-[80px]">
                    <div className="text-4xl font-black text-orange-500">{timeRemaining?.days || 0}</div>
                    <div className="text-xs text-gray-500 uppercase">Days</div>
                  </div>
                  <div className="bg-black/50 rounded-xl p-4 min-w-[80px]">
                    <div className="text-4xl font-black text-orange-500">{timeRemaining?.hours || 0}</div>
                    <div className="text-xs text-gray-500 uppercase">Hours</div>
                  </div>
                  <div className="bg-black/50 rounded-xl p-4 min-w-[80px]">
                    <div className="text-4xl font-black text-orange-500">{timeRemaining?.minutes || 0}</div>
                    <div className="text-xs text-gray-500 uppercase">Minutes</div>
                  </div>
                  <div className="bg-black/50 rounded-xl p-4 min-w-[80px]">
                    <div className="text-4xl font-black text-white">{timeRemaining?.seconds || 0}</div>
                    <div className="text-xs text-gray-500 uppercase">Seconds</div>
                  </div>
                </div>
              )}
              
              <Button
                onClick={handleEnroll}
                className="bg-orange-500 hover:bg-orange-600 text-black font-black text-lg px-10 py-6 uppercase tracking-wider"
                data-testid="sprint-enroll-btn"
              >
                {enrolled ? 'View My Sprint Dashboard' : 'Enroll in 30-Day Sprint'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sprint Definition */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-black text-white mb-4">
            THE <span className="text-orange-500">30-DAY INTENSIVE</span>
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto mb-4">
            A high-intensity, structured period of <span className="text-orange-500 font-bold">"Academic and Brand Conditioning"</span> designed 
            to bridge the gap between an athlete's physical talent and their professional eligibility.
          </p>
          <p className="text-gray-400 max-w-2xl mx-auto">
            It is the <span className="text-white font-bold">"Pre-Season"</span> for your collegiate and NIL career.
          </p>
        </div>

        {/* 4-Week Timeline */}
        <Card className="bg-gray-900/50 border-gray-800 mb-12">
          <CardContent className="p-8">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              THE THREE-PILLAR INTENSIVE
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {SPRINT_WEEKS.map((week) => {
                const IconComponent = week.icon;
                const colorMap = {
                  orange: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
                  blue: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
                  purple: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
                  green: 'bg-green-500/20 text-green-500 border-green-500/30'
                };
                
                return (
                  <div key={week.week} className="flex items-start gap-4 p-4 bg-black/30 rounded-lg">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${colorMap[week.color].split(' ').slice(0, 2).join(' ')}`}>
                      <IconComponent className={`w-6 h-6 ${colorMap[week.color].split(' ')[1]}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-bold text-sm ${colorMap[week.color].split(' ')[1]}`}>WEEK {week.week}</span>
                        <span className="text-white font-bold">{week.title}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{week.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* What is the Sprint Info Box */}
        <Card className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border-2 border-orange-500/30 mb-12">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Zap className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <h3 className="text-xl font-black text-white mb-3">WHAT IS THE SPRINT?</h3>
                <p className="text-gray-300 leading-relaxed">
                  <span className="text-orange-500 font-bold">"</span>
                  The Sprint isn't just a course; it's a <span className="text-orange-500 font-bold">commitment</span>. 
                  For 30 days, we treat your academics and your brand with the same intensity you bring to the court. 
                  You will receive daily "Playbook" tasks, spend 30 minutes in the IXL Academic Engine, and work directly 
                  on your Legacy Portfolio. At the end of 30 days, you don't just have a certificate; you have a 
                  <span className="text-white font-bold"> Power Score</span> that proves to scouts and recruiters that you are a professional.
                  <span className="text-orange-500 font-bold">"</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sprint Progress Tracker Placeholder */}
        <Card className="bg-gray-900 border-gray-800 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-500" />
              Sprint Progress Tracker
            </CardTitle>
          </CardHeader>
          <CardContent>
            {enrolled ? (
              <div className="space-y-6">
                {/* Week Progress */}
                {[1, 2, 3, 4].map((week) => (
                  <div key={week} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 font-medium">Week {week}</span>
                      <span className="text-orange-500 text-sm">0% Complete</span>
                    </div>
                    <Progress value={0} className="h-2 bg-gray-800" />
                  </div>
                ))}
                
                <div className="pt-4 border-t border-gray-800">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Overall Sprint Progress</span>
                    <span className="text-white font-bold">0 / 30 Days</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-10 h-10 text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Start Your Sprint?</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Enroll now to unlock your personalized 30-Day Sprint dashboard and begin your diagnostic journey.
                </p>
                <Button
                  onClick={handleEnroll}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                >
                  Start Here
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resources Section */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Sprint Playbook Download */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">Sprint Playbook</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Download your complete guide to the 30-Day Sprint including daily schedules, assessment rubrics, and success metrics.
                  </p>
                  <Button
                    onClick={handleDownloadPlaybook}
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Start Guide */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">Start Here</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    New to the Sprint? Follow these steps to get started on your diagnostic journey.
                  </p>
                  <ol className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-orange-500 text-black rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      Complete your initial diagnostic
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-orange-500/50 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      Review your Power Score report
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      Begin Week 1 training modules
                    </li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA Banner */}
        <Card className="bg-gradient-to-r from-orange-500/20 to-amber-500/10 border-2 border-orange-500/50 mt-8">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-black text-white mb-2">
              READY TO DISCOVER YOUR <span className="text-orange-500">BASELINE?</span>
            </h3>
            <p className="text-gray-400 mb-6">
              The 30-Day Sprint is your launchpad to the Winter '26 Cohort. Don't start behind.
            </p>
            <Button
              onClick={handleEnroll}
              className="bg-orange-500 hover:bg-orange-600 text-black font-black text-lg px-10 py-6 uppercase tracking-wider"
            >
              Secure Your Spot
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ThirtyDaySprintEnrollment;
